import React from 'react';

import { CHART_COLORS, Hsla } from 'dashboards/services/color';
import {
  InferenceDetection,
  InferenceResults,
  useImageInference,
} from 'files/hooks/useImageInference';
import { ImageSearchType } from 'files/hooks/useFilesSearch';

import { useHasAccess } from 'hooks/useHasAccess';

import * as Dialog from 'components/Dialog';
import { Button } from 'components/Button/Button';
import { Dropzone } from 'components/Dropzone/Dropzone';
import { Heading } from 'components/Heading/Heading';
import { Text } from 'components/Text/Text';

import { AddFaceToWatchlistDialog } from './AddFaceToWatchlistDialog';
import { DetectionCrop } from './DetectionCrop';
import { DetectionOverlay, LabeledDetection } from './DetectionOverlay';
import {
  cropKey,
  DetectionCropEntry,
  useDetectionCrops,
} from './useDetectionCrops';
import { WatchlistItemPicker } from './WatchlistItemPicker';

type ImageSearchDialogProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (
    embedding: number[],
    type: ImageSearchType,
    sourceFile: File
  ) => void;
  initialFile?: File | null;
};

function pickColor(index: number): Hsla {
  return CHART_COLORS[index % CHART_COLORS.length];
}

export function ImageSearchDialog({
  open,
  onClose,
  onSelect,
  initialFile,
}: ImageSearchDialogProps) {
  const { inferAll, isReady, isLoading, error } = useImageInference();
  const [hasAccess] = useHasAccess();
  const canDeploy = hasAccess('deploy_edit');

  const [file, setFile] = React.useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [results, setResults] = React.useState<InferenceResults>({
    people: [],
    faces: [],
  });
  const [hoveredKey, setHoveredKey] = React.useState<string | null>(null);
  const [localError, setLocalError] = React.useState<string | null>(null);

  // Pending face-to-watchlist state
  const [pendingFace, setPendingFace] = React.useState<{
    crop: File;
    previewUrl: string;
  } | null>(null);

  // Set file from initialFile prop when dialog opens with one
  React.useEffect(() => {
    if (initialFile) {
      setFile(initialFile);
    }
  }, [initialFile]);

  React.useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Auto-detect people and faces when the file changes
  React.useEffect(() => {
    if (!file || !isReady) return;

    let cancelled = false;
    setResults({ people: [], faces: [] });

    async function detect() {
      const detected = await inferAll(file!);
      if (!cancelled) {
        setResults(detected);
      }
    }

    detect();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, isReady]);

  const labeledForCrops = React.useMemo(
    () => [
      ...results.people.map((d) => ({ kind: 'person' as const, detection: d })),
      ...results.faces.map((d) => ({ kind: 'face' as const, detection: d })),
    ],
    [results]
  );
  const cropUrls = useDetectionCrops(previewUrl, labeledForCrops);
  const cropsReady =
    labeledForCrops.length > 0 && cropUrls.size === labeledForCrops.length;

  function handleFileChange(files: File[]) {
    if (files.length > 0) {
      setLocalError(null);
      setFile(files[0]);
    }
  }

  function handleWatchlistFaceSelect(imageUrl: string) {
    // Convert a remote image URL to a local File via canvas. The canvas
    // approach re-encodes the image (unavoidable — canvas can't extract
    // the original bytes) but that's fine for inference which re-processes
    // anyway. Requires the image server to send CORS headers (configured
    // in production, may be missing in dev/preview environments).
    setLocalError(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (!blob) return;
        setFile(new File([blob], 'watchlist-face.png', { type: blob.type }));
      });
    };
    img.onerror = () => {
      setLocalError(
        'Unable to load the reference image. This may be caused by ' +
          'cross-origin restrictions in this environment. Try uploading the ' +
          'image directly instead.'
      );
    };
    img.src = imageUrl;
  }

  function handleDetectionSelect(
    detection: InferenceDetection,
    kind: 'person' | 'face'
  ) {
    const cached = cropUrls.get(cropKey(kind, detection.detection_id));
    if (!cached) return;
    const reidType: ImageSearchType =
      kind === 'person' ? 'person_reid' : 'face_reid';
    onSelect(detection.embedding, reidType, cached.file);
    onClose();
  }

  function handleAddFaceToWatchlist(detection: InferenceDetection) {
    const cached = cropUrls.get(cropKey('face', detection.detection_id));
    if (!cached) return;
    setPendingFace({ crop: cached.file, previewUrl: cached.blobUrl });
  }

  const hasFile = Boolean(file && previewUrl);
  const hasAnyDetections =
    results.people.length > 0 || results.faces.length > 0;
  const doneLoading = hasFile && !isLoading;

  const { labeledDetections, personColors, faceColors } = React.useMemo(() => {
    const labeled: LabeledDetection[] = [];
    const personColorMap = new Map<string, Hsla>();
    const faceColorMap = new Map<string, Hsla>();
    let index = 0;

    for (const detection of results.people) {
      const color = pickColor(index++);
      personColorMap.set(detection.detection_id, color);
      labeled.push({ kind: 'person', detection, color });
    }
    for (const detection of results.faces) {
      const color = pickColor(index++);
      faceColorMap.set(detection.detection_id, color);
      labeled.push({ kind: 'face', detection, color });
    }
    return {
      labeledDetections: labeled,
      personColors: personColorMap,
      faceColors: faceColorMap,
    };
  }, [results]);

  return (
    <>
      <Dialog.Root
        open={open}
        onOpenChange={(isOpen) => !isOpen && onClose()}
        className="min-w-lg max-w-4xl"
      >
        <Dialog.Title asChild>
          <Heading level="2">Appearance search</Heading>
        </Dialog.Title>

        <div className="mt-1 flex flex-col gap-3">
          {hasFile && previewUrl ? (
            <div className="flex flex-col gap-4">
              <div className="flex justify-center">
                <DetectionOverlay
                  imageUrl={previewUrl}
                  detections={labeledDetections}
                  isLoading={isLoading}
                  cropsReady={cropsReady}
                  hoveredKey={hoveredKey}
                  onHoverChange={setHoveredKey}
                  onSelect={({ detection, kind }) =>
                    handleDetectionSelect(detection, kind)
                  }
                />
              </div>

              {doneLoading && (
                <div className="flex flex-col gap-3">
                  <DetectionRow
                    title="People"
                    kind="person"
                    detections={results.people}
                    colors={personColors}
                    cropUrls={cropUrls}
                    cropsReady={cropsReady}
                    hoveredKey={hoveredKey}
                    onHoverChange={setHoveredKey}
                    onSearch={(detection) =>
                      handleDetectionSelect(detection, 'person')
                    }
                  />
                  <DetectionRow
                    title="Faces"
                    kind="face"
                    detections={results.faces}
                    colors={faceColors}
                    cropUrls={cropUrls}
                    cropsReady={cropsReady}
                    hoveredKey={hoveredKey}
                    onHoverChange={setHoveredKey}
                    onSearch={(detection) =>
                      handleDetectionSelect(detection, 'face')
                    }
                    onAddToWatchlist={
                      canDeploy ? handleAddFaceToWatchlist : undefined
                    }
                  />
                </div>
              )}

              <div className="flex items-center justify-between gap-4">
                <div>
                  {doneLoading && hasAnyDetections && (
                    <Text size="small" className="opacity-70">
                      {results.people.length} person(s) and{' '}
                      {results.faces.length} face(s) detected. Click a crop to
                      search for similar.
                    </Text>
                  )}
                  {doneLoading && !hasAnyDetections && !error && (
                    <Text size="small" className="opacity-70">
                      No people or faces detected.
                    </Text>
                  )}
                </div>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    setFile(null);
                    setResults({ people: [], faces: [] });
                    setHoveredKey(null);
                  }}
                >
                  Change image
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <Dropzone accept="image/*" onChange={handleFileChange} />

              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-neutral-300 dark:bg-neutral-700" />
                <Text size="small" className="opacity-50">
                  or search by watchlist item
                </Text>
                <div className="h-px flex-1 bg-neutral-300 dark:bg-neutral-700" />
              </div>

              <WatchlistItemPicker onSelect={handleWatchlistFaceSelect} />
            </div>
          )}

          {(error || localError) && (
            <Text size="small" intent="danger">
              {error || localError}
            </Text>
          )}
        </div>
      </Dialog.Root>

      {pendingFace && (
        <AddFaceToWatchlistDialog
          open={Boolean(pendingFace)}
          onClose={() => setPendingFace(null)}
          faceCrop={pendingFace.crop}
          facePreviewUrl={pendingFace.previewUrl}
        />
      )}
    </>
  );
}

type DetectionRowProps = {
  title: string;
  kind: 'person' | 'face';
  detections: InferenceDetection[];
  colors: Map<string, Hsla>;
  cropUrls: Map<string, DetectionCropEntry>;
  cropsReady: boolean;
  hoveredKey: string | null;
  onHoverChange: (key: string | null) => void;
  onSearch: (detection: InferenceDetection) => void;
  onAddToWatchlist?: (detection: InferenceDetection) => void;
};

function DetectionRow({
  title,
  kind,
  detections,
  colors,
  cropUrls,
  cropsReady,
  hoveredKey,
  onHoverChange,
  onSearch,
  onAddToWatchlist,
}: DetectionRowProps) {
  return (
    <div className="flex flex-col gap-1">
      <Text size="small" className="font-semibold opacity-70">
        {title} ({detections.length})
      </Text>
      {detections.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {detections.map((detection) => {
            const key = cropKey(kind, detection.detection_id);
            return (
              <DetectionCrop
                key={key}
                cropUrl={cropUrls.get(key)?.blobUrl}
                detection={detection}
                color={colors.get(detection.detection_id)!}
                kind={kind}
                disabled={!cropsReady}
                isHovered={hoveredKey === key}
                onHoverEnter={() => onHoverChange(key)}
                onHoverLeave={() => onHoverChange(null)}
                onSearch={() => onSearch(detection)}
                onAddToWatchlist={
                  onAddToWatchlist
                    ? () => onAddToWatchlist(detection)
                    : undefined
                }
              />
            );
          })}
        </div>
      ) : (
        <Text size="small" className="opacity-50">
          None detected.
        </Text>
      )}
    </div>
  );
}
